"use client";

import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { LocaleSwitcher } from "@/components/i18n/locale-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Logo } from "@/components/brand/logo";
import { useAuthStore } from "@/lib/stores/auth-store";

const NAV_ITEMS = [
  { key: "howItWorks", href: "/#how" },
  { key: "features", href: "/#features" },
  { key: "pricing", href: "/#pricing" },
  { key: "docs", href: "/docs" },
  { key: "faq", href: "/#faq" },
] as const;

export function Navbar() {
  const t = useTranslations("marketing.navbar");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Marketing pages never asked who the visitor was, so a signed-in user was
  // still offered "Log in" and "Start free trial" here, while the pricing
  // section on the same page correctly showed their current plan. loadUser only
  // ran from the dashboard and admin layouts.
  const { isAuthenticated, isLoading, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Signed out is the safe default while the profile request is in flight: it
  // matches what the server rendered, so there is no hydration mismatch, and a
  // visitor who really is signed out never sees the wrong thing.
  const showAccountLinks = !isLoading && isAuthenticated;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b bg-background/70 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center gap-5 px-6">
        <Link href="/" onClick={() => setMobileOpen(false)} className="text-lg">
          <Logo />
        </Link>

        {/* Desktop links */}
        <div className="ms-auto hidden items-center gap-6 lg:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t(item.key)}
            </Link>
          ))}
        </div>

        <div className="ms-auto flex items-center gap-3 lg:ms-0">
          <ThemeToggle />
          <LocaleSwitcher />
          {showAccountLinks ? (
            <Button asChild variant="gradient" size="sm" className="hidden sm:inline-flex">
              <Link href="/overview">{t("dashboard")}</Link>
            </Button>
          ) : (
            <>
              <Link
                href="/login"
                className="hidden whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground lg:block"
              >
                {t("logIn")}
              </Link>
              <Button asChild variant="gradient" size="sm" className="hidden sm:inline-flex">
                <Link href="/register">{t("startFreeTrial")}</Link>
              </Button>
            </>
          )}
          {/* Burger below 1024px */}
          <Button
            variant="glass"
            size="icon-sm"
            className="lg:hidden"
            aria-label={t("menuLabel")}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </Button>
        </div>
      </nav>

      {/* Mobile menu: solid panel, links + both CTAs */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="border-b bg-popover px-6 pb-6 pt-2 shadow-2xl lg:hidden"
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="block border-b border-border/50 py-3 text-[15px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {t(item.key)}
            </Link>
          ))}
          <div className="mt-4 flex flex-col gap-3">
            {showAccountLinks ? (
              <Button asChild variant="gradient" onClick={() => setMobileOpen(false)}>
                <Link href="/overview">{t("dashboard")}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="glass" onClick={() => setMobileOpen(false)}>
                  <Link href="/login">{t("logIn")}</Link>
                </Button>
                <Button asChild variant="gradient" onClick={() => setMobileOpen(false)}>
                  <Link href="/register">{t("startFreeTrial")}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
